/**
 * @module app.um
 */

import { autoinject, bindingMode, TaskQueue } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IAuthSearchFilters } from 'app/um/services/auth-service';
import { IMemberSearchFilters } from 'app/cd/services/member-service';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { IAuthSearchOptions } from 'app/um/components/auth/auth-filters';
import { fireEvent } from 'app/shared/lib/fire-events';
import { guid } from 'app/shared/lib/guid';
import { countMemberFilters } from 'app/cd/services/member-service';
import { countAuthFilters } from 'app/um/services/auth-service';
import { Toaster } from 'app/shared/lib/toaster';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SetFocusEvent } from 'app/shared/lib/events';
import { NamespaceOptions } from 'app/shared/lib/i18n';
import { isNullOrUndefined } from 'app/shared/lib/utilities';

interface IAuthSearchCustomEvent { authSearchFilters: IAuthSearchFilters, memberSearchFilters: IMemberSearchFilters }
export type AuthSearchCustomEvent = CustomEvent<IAuthSearchCustomEvent>;

@autoinject()
export class AuthSearchFormCustomElement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public authSearchFilters: IAuthSearchFilters = {};

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public tempAuthSearchFilters: IAuthSearchFilters = {};

    @bindable.objectNull()
    public authSearchOptions: IAuthSearchOptions | null = null;

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public memberSearchFilters: IMemberSearchFilters = {};

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public tempMemberSearchFilters: IMemberSearchFilters = {};

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public defaultCollapsed: boolean = false;

    @bindable.objectNull()
    public authAvailableFilters: string[] | null = null;

    @bindable.objectNull()
    public memberAvailableFilters: string[] | null = null;

    @bindable.string()
    public i18nNamespace: Extract<NamespaceOptions, NamespaceOptions.CD | NamespaceOptions.PP> = NamespaceOptions.CD;

    protected authInformationId = guid();
    protected memberInformationId = guid();

    protected id: guid = guid();


    ////****************** Element References ***************************************////
    private $collapsible!: ICollapse;
    protected $searchCriteriaHeader!: HTMLElement;
    protected $currentCriteriaHeader!: HTMLElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly taskQueue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected attached(): void {
        if(this.defaultCollapsed) { return; }

        this.taskQueue.queueTask(() => {
            this.showAvailableCriteria();
        });
    }

    ////****************** Events ***************************************************////
    protected handleSubmit(event?: Event): void {
        if(!this.state.saving && !this.state.loading) {
            this.fireSearch();
        }
    }

    protected handleKeyDown(e: KeyboardEvent): boolean {
        if(e.keyCode === 13 && e.ctrlKey) {
            this.handleSubmit();
            return false;
        }
        return true;
    }


    ////****************** Functions ************************************************////
    public showAvailableCriteria(): void {
        this.$collapsible.open(0);
        this.$collapsible.close(1);
    }

    public showSelectedCriteria(): void {
        this.$collapsible.close(0);
        this.$collapsible.open(1);
    }

    protected fireSearch(): void {

        const authFilterCount = countAuthFilters(this.tempAuthSearchFilters);
        const memberFilterCount = countMemberFilters(this.tempMemberSearchFilters);
        if(authFilterCount + memberFilterCount === 0) {
            this.logger.warn('Invalid or missing search criteria');
            this.toaster.standard.searchNoCriteria();
            return;
        }

        if(isNullOrUndefined(this.tempAuthSearchFilters.dueDateEnd)) {
            this.tempAuthSearchFilters.dueDateEnd = this.tempAuthSearchFilters.dueDateStart;
        }

        if(isNullOrUndefined(this.tempAuthSearchFilters.requestedDateEnd)) {
            this.tempAuthSearchFilters.requestedDateEnd = this.tempAuthSearchFilters.requestedDateStart;
        }

        if(isNullOrUndefined(this.tempAuthSearchFilters.nextReviewDateEnd)) {
            this.tempAuthSearchFilters.nextReviewDateEnd = this.tempAuthSearchFilters.nextReviewDateStart;
        }

        const detail: IAuthSearchCustomEvent = {
            authSearchFilters: this.tempAuthSearchFilters,
            memberSearchFilters: this.tempMemberSearchFilters
        }
        fireEvent(this.$element, 'search', detail);
        this.showSelectedCriteria();
        this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
    }


    public clear(returnFocus?: boolean): void {
        this.authSearchFilters = {};
        this.memberSearchFilters = {};
        this.tempAuthSearchFilters = {};
        this.tempMemberSearchFilters = {};
        this.showAvailableCriteria();
        fireEvent(this.$element, 'clear-results');
        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }
}
